import React, { useEffect } from "react";
import Card from "components/card";
import DeleteIcon from "../../../../assets/imgesDriver/deleteIcon.svg";
import block from "../../../../assets/svg/block.svg";
import unblock from "../../../../assets/svg/unblock.svg";
import ButtonEdit from "../../../../assets/svg/ButtonEdit.svg";
import "../riders.css";
import { useTranslation } from "react-i18next";
import { DTO, Types } from "shared-lib";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { FaCaretDown, FaCaretUp, FaEye } from "react-icons/fa";

type RowObj = DTO.RiderDTO;

const columnHelper = createColumnHelper<RowObj>();

function ColumnsTableRiders(props: {
  tableData: any;
  handleClickForDeleteModal: (data: any) => void;
  handleToggleForStatusModal: (data: any) => void;
  handleViewRider: (data: any) => void;
}) {
  const { tableData, handleClickForDeleteModal, handleToggleForStatusModal, handleViewRider } =
    props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { t } = useTranslation();
  const columns = [
    columnHelper.accessor("firstName", {
      id: "name",
      header: () => (
        <p className="text-start text-sm font-bold text-gray-300">
          {t("Rider Name")}
        </p>
      ),
      cell: (info: any) => {
        const row = info.row.original as DTO.RiderDTO;
        const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_URL}/uploads/`;
        const hasImage = !!row.profileImageKey;
        return (
          <p
            className="text-start text-sm font-bold text-white"
            style={{ display: "flex", alignItems: "center" }}
          >
            {hasImage ? (
              <img
                src={`${BASE_IMAGE_URL}${row.profileImageKey}`}
                alt="Profile"
                className="w-10 h-10 rounded-full mr-3"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  padding: "1px",
                  borderRadius: "8px",
                  display: "inline-block",
                  marginRight: "6px",
                  backgroundColor: "rgba(255,255,255,0.1)",
                }}
              >
                <span
                  style={{
                    position: "relative",
                    left: "14px",
                    top: "8.5px",
                    fontSize: "18px",
                    color: "#a0aec0",
                  }}
                >
                  {info.getValue() ? info.getValue().slice(0, 1).toUpperCase() : ""}
                </span>
              </div>
            )}
            <span className="text-white">{info.getValue()}</span>
          </p>
        );
      },
    }),
    columnHelper.accessor("mobileNumber", {
      id: "mobileNumber",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Mobile Number")}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("totalRidesCompleted", {
      id: "totalRidesCompleted",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Total Rides Completed")}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "riderStatus",
      header: () => (
        <p className="text-start text-sm font-bold text-gray-300">
          {t("Status")}
        </p>
      ),
      cell: (info) => (
        <p
          className={
            info.getValue() === Types.RiderStatusEnum.ACTIVE
              ? "onlineClass text-sm font-bold"
              : "offlineClass text-sm font-bold"
          }
        >
          {info.getValue() === Types.RiderStatusEnum.ACTIVE
            ? t("Active")
            : t("Inactive")}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "actions",
      header: () => (
        <p className="text-start text-sm font-bold text-gray-300">
          {t("action")}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-2">
          <div
            className="cursor-pointer"
            title={t("Visualizar")}
            onClick={() => handleViewRider(info.row.original)}
          >
            <FaEye size={18} color="#a0aec0" />
          </div>
          {info.getValue() === Types.RiderStatusEnum.ACTIVE ? (
            <div className="cursor-pointer" title={t("Bloquear")}>
              <img
                style={{ marginRight: "4px" }}
                src={unblock}
                alt={t("Desbloquear")}
                onClick={() => {
                  handleToggleForStatusModal(info.row.original);
                }}
              />
            </div>
          ) : (
            <div className="cursor-pointer" title={t("Desbloquear")}>
              <img
                style={{ marginRight: "4px" }}
                src={block}
                alt={t("Bloquear")}
                onClick={() => {
                  handleToggleForStatusModal(info.row.original);
                }}
              />
            </div>
          )}
          <div className="cursor-pointer" title={t("Excluir")}>
            <img
              src={DeleteIcon}
              alt={t("Excluir")}
              onClick={() => handleClickForDeleteModal(info.row.original)}
            />
          </div>
        </div>
      ),
    }),
  ]; // eslint-disable-next-line
  const [data, setData] = React.useState([...tableData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-white">
          {t("Riders")}
        </div>
      </header>
      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-white/10">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer pb-3 pr-4 pt-4 text-start"
                    >
                      <div className="flex gap-4 text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {
                          <>
                            {header.column.getIsSorted() === "asc" ? (
                              <FaCaretUp
                                size={20}
                                className="font-bold text-gray-600"
                              />
                            ) : header.column.getIsSorted() === "desc" ? (
                              <FaCaretDown
                                size={20}
                                className="font-bold text-gray-600"
                              />
                            ) : (
                              <div>
                                <FaCaretDown
                                  size={20}
                                  className="font-bold text-gray-600"
                                />
                              </div>
                            )}
                          </>
                        }
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {tableData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  <h2 className="m-4 text-gray-400" style={{ fontSize: "30px" }}>
                    {t("No Results!")}
                  </h2>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table
                .getRowModel()
                .rows.slice(0, 10)
                .map((row, idx) => {
                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                        }`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className="min-w-[20%] border-b border-white/5 py-3 pr-4 text-start"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          )}
        </table>
      </div>
    </Card>
  );
}

export default ColumnsTableRiders;
